import { COUNTRIES } from "../../../../constants/countries";
import { BranchStatus } from "../../../../constants/branch-status";
import { EditBranchDTO, BranchBasicInfo, BranchSettings } from "../../../../services/dto/request/branches-request-dto";
import { ManagerInfo } from "../../../../services/dto/request/branches-request-dto";

interface BuildEditParamsOptions {
  countryCode: string;
  branchId: number;
  branchBasicInfo: BranchBasicInfo;
  branchSettings: BranchSettings;
  managerInfo: ManagerInfo;
  updatedReviewLink: string | null;
  branchStatus: number;
  paycloudEntityId: number;
  paycloudUserId: number;
  compressedAfipFile: File | null;
  compressedManagerDniFront: File | null;
  compressedManagerDniBack: File | null;
  compressedManagerSelfie: File | null;
}

/**
 * Construye los parámetros para editar una sucursal según el país
 * Cada país puede tener campos obligatorios u opcionales diferentes
 */
export const buildEditBranchParamsByCountry = async (
  options: BuildEditParamsOptions
): Promise<EditBranchDTO> => {
  const {
    countryCode,
    branchId,
    branchBasicInfo,
    branchSettings,
    managerInfo,
    updatedReviewLink,
    branchStatus,
    paycloudEntityId,
    paycloudUserId,
    compressedAfipFile,
    compressedManagerDniFront,
    compressedManagerDniBack,
    compressedManagerSelfie,
  } = options;

  // Campos comunes a todos los países
  const commonParams = {
    BranchId: branchId,
    Address: branchBasicInfo.address,
    Name: branchBasicInfo.name,
    FantasyName: branchBasicInfo.fantasyName,
    Phone: branchBasicInfo.phone,
    EqualSplitTip: branchSettings.equalSplitTip,
    GoogleLink: updatedReviewLink,
    IsSameAddress: branchBasicInfo.sameAddress,
    OnlyRatings: branchSettings.onlyRatings,
    Status: branchStatus,
    IsInCharge: managerInfo.isInCharge,
    ManagerName: managerInfo.managerName,
    ManagerLastName: managerInfo.managerLastName,
    ManagerDocumentType: managerInfo.managerDocumentType,
    ManagerDocumentNumber: managerInfo.managerDocumentNumber,
    ManagerPhone: managerInfo.managerPhone,
    ManagerAddress: managerInfo.managerAddress,
    ManagerProvinceId: managerInfo.managerProvinceId,
    ManagerCityId: managerInfo.managerCityId,
    managerDniFront: compressedManagerDniFront,
    managerDniBack: compressedManagerDniBack,
    managerSelfie: compressedManagerSelfie,
    PaycloudEntityId: paycloudEntityId,
    PaycloudUserId: paycloudUserId,
  };

  switch (countryCode) {
    case COUNTRIES.DO: // República Dominicana
      return {
        ...commonParams,
        // RD no requiere cuenta de propinas
        WithTipAccount: false,
        
        // RD no requiere CUIT/CUIL
        IdentityType: "CUIT" as any,
        IdentityNumber: "",
        
        // RD no requiere campos de dirección de actividad
        ActivityAddress: "",
        ActivityFloor: null,
        ActivityPostalCode: "",
        ActivityDepartment: null,
        ActivityProvinceId: 0,
        ActivityCityId: 0,
        
        // RD no requiere campos de dirección principal detallados
        Floor: null,
        PostalCode: "",
        Department: null,
        ProvinceId: 0,
        CityId: 0,
        
        // RD no usa AFIP
        afipFile: null,
        
        // RD no requiere persona física/contribuyente
        IsPhysicalPerson: false,
        TaxPayer: false,
      };

    case COUNTRIES.AR: // Argentina
    default:
      return {
        ...commonParams,
        // Argentina sí puede tener cuenta de propinas
        WithTipAccount: branchSettings.createCBU,
        
        // Campos específicos de Argentina
        IdentityType: branchSettings.identityType,
        IdentityNumber: branchSettings.identityNumber,
        IsPhysicalPerson: branchSettings.personType === "PHYSICAL",
        TaxPayer: branchSettings.isTaxPayer,
        
        // Campos de dirección de actividad (AR sí los usa)
        ActivityAddress: branchBasicInfo.sameAddress ? branchBasicInfo.address : branchBasicInfo.activityAddress ?? "",
        ActivityFloor: branchBasicInfo.sameAddress ? branchBasicInfo.floor : branchBasicInfo.activityFloor ?? null,
        ActivityPostalCode: branchBasicInfo.sameAddress ? branchBasicInfo.postalCode : branchBasicInfo.activityPostalCode ?? "",
        ActivityDepartment: branchBasicInfo.sameAddress ? branchBasicInfo.department : branchBasicInfo.activityDepartment ?? null,
        ActivityProvinceId: branchBasicInfo.sameAddress ? branchBasicInfo.branchProvinceId : branchBasicInfo.activityProvinceId ?? 0,
        ActivityCityId: branchBasicInfo.sameAddress ? branchBasicInfo.branchCityId : branchBasicInfo.activityCityId ?? 0,
        
        // Campos de dirección principal
        Floor: branchBasicInfo.floor || null,
        PostalCode: branchBasicInfo.postalCode,
        Department: branchBasicInfo.department || null,
        ProvinceId: branchBasicInfo.branchProvinceId,
        CityId: branchBasicInfo.branchCityId,
        
        // Archivo AFIP
        afipFile: compressedAfipFile,
        ...(branchStatus === BranchStatus.INCOMPLETE
          ? {
              BirthDate: branchBasicInfo.birthDate,
              Occupation: branchBasicInfo.occupation,
              IvaCondition: branchBasicInfo.ivaCondition,
              IsPEP: branchBasicInfo.isPEP,
              PepPosition: branchBasicInfo.isPEP ? branchBasicInfo.pepPosition : null,
              IsUIF: branchBasicInfo.isUIF,
              Gender: branchBasicInfo.gender,
              CivilStatus: branchBasicInfo.civilStatus,
            }
          : {}),
      };
  }
};

